import { useQuery } from "@tanstack/react-query";
import type { ColumnsType } from "antd/es/table";
import { journalsQueries } from "@/entities/journal";

export type JournalBulkSendSection = 1 | 2 | 3 | 4;

export type BulkSendItemPayload = {
  itemNoVersionId: string;
  selected: boolean;
  partId?: string;
  sequenceNumber?: number | string;
};

export type JournalBulkSendToSignModalProps = {
  open: boolean;
  onClose: () => void;
  section: JournalBulkSendSection;
  signersList?: unknown[];
  isSending?: boolean;
  onSendBatch: (items: BulkSendItemPayload[]) => void | Promise<void>;
  sectionOneId?: string;
  sectionTwoId?: string;
  sectionTwoListId?: string;
  sectionThreeListId?: string;
  sectionFourListId?: string;
  resolveTable: (data: unknown) => BulkSendResolvedTable;
};

export type BulkSendResolvedTable = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- section tables use different row shapes
  columns: ColumnsType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- section tables use different row shapes
  dataSource: any[];
  rowKey?: string;
  tableClassName?: string;
};

export function getBulkSendListTotal(data: unknown): number {
  if (!data || typeof data !== "object") {
    return 0;
  }
  return (data as { total?: number }).total ?? 0;
}

export const BULK_SEND_MAX_SELECTION_SECTION_FOUR = 30;
const BULK_SEND_STATUS_NEW = "NEW";

type BulkSendListParams = {
  section: JournalBulkSendSection;
  enabled: boolean;
  page: number;
  limit: number;
  search: string;
  sectionOneId?: string;
  sectionTwoId?: string;
  sectionTwoListId?: string;
  sectionThreeListId?: string;
  sectionFourListId?: string;
  dateStart?: string;
  dateEnd?: string;
  signerIds?: string[];
  specialJournalDate?: string;
  dateHandover?: string;
  workTypes?: string[];
  actConnect?: boolean;
  defectDeadline?: string;
  eliminationDate?: string;
};

export function useBulkSendListQuery(params: BulkSendListParams) {
  const {
    section,
    enabled,
    page,
    limit,
    search,
    sectionOneId,
    sectionTwoId,
    sectionTwoListId,
    sectionThreeListId,
    sectionFourListId,
    dateStart,
    dateEnd,
    signerIds = [],
    specialJournalDate,
    dateHandover,
    workTypes = [],
    actConnect,
    defectDeadline,
    eliminationDate,
  } = params;

  const status = BULK_SEND_STATUS_NEW;
  const signerId = signerIds.join(",");
  const sectionTwoRequestId = sectionTwoId || sectionTwoListId || "";

  const sectionOneQuery = useQuery({
    ...journalsQueries.getSection1({
      sectionOneId: sectionOneId || "",
      search,
      page,
      limit,
      status,
      dateFrom: dateStart,
      dateTo: dateEnd,
    }),
    enabled: enabled && section === 1 && !!sectionOneId,
  });

  const sectionTwoQuery = useQuery({
    ...journalsQueries.getSection2({
      sectionTwoId: sectionTwoRequestId,
      search,
      page,
      limit,
      status,
      specialJournalDate,
      dateHandover,
      signerId,
    }),
    enabled: enabled && section === 2 && !!sectionTwoRequestId,
  });

  const sectionTwoFilteredList = sectionTwoQuery.data?.section2List ?? [];
  const sectionTwoFilteredTotal = sectionTwoQuery.data?.total ?? 0;
  const shouldUseSectionTwoFallback =
    sectionTwoQuery.isSuccess &&
    sectionTwoFilteredTotal > sectionTwoFilteredList.length &&
    sectionTwoFilteredList.length < limit;

  const sectionTwoFallbackQuery = useQuery({
    ...journalsQueries.getSection2({
      sectionTwoId: sectionTwoRequestId,
      search,
      page: 1,
      limit: Math.max(sectionTwoFilteredTotal, limit),
      specialJournalDate,
      dateHandover,
      signerId,
    }),
    enabled:
      enabled &&
      section === 2 &&
      !!sectionTwoRequestId &&
      shouldUseSectionTwoFallback,
    staleTime: 30_000,
  });

  const sectionTwoFallbackItems = (
    sectionTwoFallbackQuery.data?.section2List ?? []
  ).filter((item) => item.status === status);
  let sectionTwoData = sectionTwoQuery.data;
  if (shouldUseSectionTwoFallback) {
    sectionTwoData = sectionTwoFallbackQuery.data
      ? {
          ...sectionTwoFallbackQuery.data,
          total: sectionTwoFallbackItems.length,
          section2List: sectionTwoFallbackItems.slice(
            (page - 1) * limit,
            page * limit,
          ),
        }
      : undefined;
  }

  const sectionThreeQuery = useQuery({
    ...journalsQueries.getSection3({
      sectionThreeListId: sectionThreeListId || "",
      search,
      page,
      limit,
      status,
      dateFrom: dateStart,
      dateTo: dateEnd,
      workType: workTypes.join(","),
      actConnect: actConnect ? "true" : undefined,
      signerId,
    }),
    enabled: enabled && section === 3 && !!sectionThreeListId,
  });

  const sectionFourQuery = useQuery({
    ...journalsQueries.getSection4({
      section4ListId: sectionFourListId || "",
      search,
      page,
      limit,
      statusPartOne: [status],
      signerId: signerIds,
      defectFixingPlanDate: defectDeadline || undefined,
      defectFixingFactDate: eliminationDate || undefined,
    }),
    enabled: enabled && section === 4 && !!sectionFourListId,
  });

  switch (section) {
    case 1:
      return { data: sectionOneQuery.data, isFetching: sectionOneQuery.isFetching };
    case 2:
      return {
        data: sectionTwoData,
        isFetching:
          sectionTwoQuery.isFetching || sectionTwoFallbackQuery.isFetching,
      };
    case 3:
      return { data: sectionThreeQuery.data, isFetching: sectionThreeQuery.isFetching };
    case 4:
      return { data: sectionFourQuery.data, isFetching: sectionFourQuery.isFetching };
    default:
      return { data: undefined, isFetching: false };
  }
}

export function getBulkSendSequenceNumber(
  section: JournalBulkSendSection,
  row: Record<string, unknown>,
): number | string | undefined {
  if (section === 1) {
    return row.number as number | undefined;
  }
  if (section === 2) {
    return row.sequenceNumber as number | undefined;
  }
  if (section === 3) {
    return (row.number ?? row.sequenceNumber) as number | undefined;
  }
  if (section === 4) {
    if (row.sequenceNumber != null) {
      return row.sequenceNumber as number;
    }
    const itemInfo = row.itemInfo as { sequenceNumber?: number } | undefined;
    return itemInfo?.sequenceNumber;
  }
  return undefined;
}

export function getBulkSendPartId(
  section: JournalBulkSendSection,
  row: Record<string, unknown>,
): string | undefined {
  if (section !== 4) return undefined;
  const itemInfo = row.itemInfo as { itemPartOne?: { id?: string } } | undefined;
  return itemInfo?.itemPartOne?.id;
}
